/**
 * 디렉터리·프로필 필드 키 → 화면 라벨
 * - 우선순위: FMS 오브젝트 attributes(/api/objects/{objectKey}/attributes)에서 만든 맵
 * - 폴백: 아래 정적 문자열(오프라인·API 실패 시)
 */

/** 카드·목록에서 쓰는 고정 행 라벨 (역할별 문구 분기, CETUS 전용) */
export const CARD_ROW_LABEL = {
  MAIN_INDUSTRY: '주요 산업군',
  TECHNICAL_FIELD: '세부 전문기술 분야',
  FIELD_INTEREST: '관심 분야',
  FIELD_TECH: '기술 분야',
  AFFILIATION_IND: '소속',
  AFFILIATION_ORG: '소속·기관',
  REGION_IND: '희망 참여 지역',
  REGION_ORG: '소재지·주소',
  CERTIFICATIONS: '보유기술 및 자격증',
  MAJOR_PROJECTS: '주요 프로젝트·논문·성과',
  EMAIL: '이메일',
  PROJECT_COUNT: '참여 과제',
  PROPOSAL_TASKS: '제안 과제',
};

/** 프로필 상단·메타 블록 (CETUS·UI 고정) */
export const PROFILE_ROW_LABEL = {
  TYPE: '유형',
  NAME: '이름',
  EMAIL: '이메일',
  PHONE: '연락처',
  AFFILIATION: '소속',
  SCHOOL: '학교명',
  MAJOR: '전공',
  INTERESTS: '관심 분야',
  CERTIFICATIONS: '보유기술 및 자격증',
  MAJOR_PROJECTS: '주요 프로젝트·논문·성과',
  PREFERENCES_FIELDS: '희망 협력 분야',
  PREFERENCES_ORG_TYPES: '선호 기관 유형',
  PREFERENCES_LOCATION: '근무·참여 가능 지역',
  PREFERENCES_WORK_TYPE: '참여 가능 형태',
  BIO: '자기소개',
  SNS: 'SNS',
  ORG_NAME: '기관·기업명',
  MAIN_INDUSTRY: '주요 산업군',
  TECHNICAL_FIELD: '세부 전문기술 분야',
  CONTACT_PHONE: '담당자·유선',
};

/** FMS 조회 실패 시 search_data 키별 폴백 (한글) */
const STATIC_FIELD_LABEL_FALLBACK_KO = {
  affiliation: '소속',
  university: '학교명',
  school_name: '학교명',
  schoolName: '학교명',
  department: '학과·부서',
  major: '전공',
  interests: '관심 분야',
  preferences_fields: '희망 협력 분야',
  preferences_org_types: '선호 기관 유형',
  preferences_location: '근무·참여 가능 지역',
  preferences_work_type: '참여 가능 형태',
  certifications: '보유기술 및 자격증',
  'major projects papers achievements': '주요 프로젝트·논문·성과',
  major_projects_papers_achievements: '주요 프로젝트·논문·성과',
  main_industry: '주요 산업군',
  technical_field: '세부 전문기술 분야',
  address: '주소',
  company_name: '기관·기업명',
  companyname: '기관·기업명',
  organization_name: '기관·기업명',
  org_name: '기관·기업명',
  bio: '자기소개',
  sns: 'SNS',
  rdneeds: 'R&D·기술 수요',
  rd_needs: 'R&D·기술 수요',
  rdNeeds: 'R&D·기술 수요',
  requiredmanpower: '필요 인력',
  required_manpower: '필요 인력',
  requiredManpower: '필요 인력',
  cooperationtype: '협력 형태',
  cooperation_type: '협력 형태',
  cooperationType: '협력 형태',
  collaborationexperience: '협력 경험',
  collaboration_experience: '협력 경험',
  collaborationExperience: '협력 경험',
};

export function normalizeFieldKey(key) {
  return String(key ?? '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
}

/** @param {import('i18next').TFunction} t */
export function getCardRowLabels(t) {
  return {
    MAIN_INDUSTRY: t('experts.fieldLabels.main_industry'),
    TECHNICAL_FIELD: t('experts.fieldLabels.technical_field'),
    FIELD_INTEREST: t('experts.fieldLabels.field_interest'),
    FIELD_TECH: t('experts.fieldLabels.field_tech'),
    AFFILIATION_IND: t('experts.fieldLabels.affiliation_ind'),
    AFFILIATION_ORG: t('experts.fieldLabels.affiliation_org'),
    REGION_IND: t('experts.fieldLabels.region_ind'),
    REGION_ORG: t('experts.fieldLabels.region_org'),
    CERTIFICATIONS: t('experts.fieldLabels.certifications'),
    MAJOR_PROJECTS: t('experts.fieldLabels.major_projects_papers_achievements'),
    EMAIL: t('experts.fieldLabels.email'),
    PROJECT_COUNT: t('experts.fieldLabels.project_count'),
    PROPOSAL_TASKS: t('experts.fieldLabels.proposal_tasks'),
  };
}

/** @param {import('i18next').TFunction} t */
export function getProfileRowLabels(t) {
  return {
    TYPE: t('experts.fieldLabels.type'),
    NAME: t('experts.fieldLabels.name'),
    EMAIL: t('experts.fieldLabels.email'),
    PHONE: t('experts.fieldLabels.phone'),
    AFFILIATION: t('experts.fieldLabels.affiliation'),
    SCHOOL: t('experts.fieldLabels.school_name'),
    MAJOR: t('experts.fieldLabels.major'),
    INTERESTS: t('experts.fieldLabels.interests'),
    CERTIFICATIONS: t('experts.fieldLabels.certifications'),
    MAJOR_PROJECTS: t('experts.fieldLabels.major_projects_papers_achievements'),
    PREFERENCES_FIELDS: t('experts.fieldLabels.preferences_fields'),
    PREFERENCES_ORG_TYPES: t('experts.fieldLabels.preferences_org_types'),
    PREFERENCES_LOCATION: t('experts.fieldLabels.preferences_location'),
    PREFERENCES_WORK_TYPE: t('experts.fieldLabels.preferences_work_type'),
    BIO: t('experts.fieldLabels.bio'),
    SNS: t('experts.fieldLabels.sns'),
    ORG_NAME: t('experts.fieldLabels.organization_name'),
    MAIN_INDUSTRY: t('experts.fieldLabels.main_industry'),
    TECHNICAL_FIELD: t('experts.fieldLabels.technical_field'),
    CONTACT_PHONE: t('experts.fieldLabels.contact_phone'),
  };
}

function lookupStaticFieldLabel(key, t) {
  if (key == null || key === '') return '';
  const raw = String(key).trim();
  const low = normalizeFieldKey(raw);
  if (t) {
    const fromI18n = t(`experts.fieldLabels.${low}`, { defaultValue: '' });
    if (fromI18n) return fromI18n;
  }
  if (STATIC_FIELD_LABEL_FALLBACK_KO[raw] !== undefined) return STATIC_FIELD_LABEL_FALLBACK_KO[raw];
  const byLower = Object.entries(STATIC_FIELD_LABEL_FALLBACK_KO).find(([k]) => normalizeFieldKey(k) === low);
  if (byLower) return byLower[1];
  return '';
}

function humanizeKey(key) {
  const raw = String(key ?? '').trim();
  if (!raw) return '';
  return raw.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

/**
 * FMS 맵에서만 조회 (없으면 빈 문자열)
 * @param {Record<string, string>} fmsMap
 * @param {string} key
 */
export function lookupFmsLabel(fmsMap, key) {
  if (!fmsMap || key == null || key === '') return '';
  const raw = String(key).trim();
  const candidates = [raw, raw.toLowerCase(), normalizeFieldKey(raw)];
  for (const c of candidates) {
    if (fmsMap[c] !== undefined && String(fmsMap[c]).trim() !== '') return String(fmsMap[c]).trim();
  }
  const low = normalizeFieldKey(raw);
  for (const k of Object.keys(fmsMap)) {
    if (normalizeFieldKey(k) === low) return String(fmsMap[k]).trim();
  }
  return '';
}

/**
 * 여러 필드 키 중 FMS에 먼저 나오는 라벨
 * @param {Record<string, string>} fmsMap
 * @param {string[]} keys
 */
export function pickFirstFmsLabel(fmsMap, keys) {
  if (!fmsMap || !Array.isArray(keys)) return '';
  for (const k of keys) {
    const L = lookupFmsLabel(fmsMap, k);
    if (L) return L;
  }
  return '';
}

/**
 * 필드 키 → 표시 라벨 (FMS 우선 → 정적 폴백 → 키 가독화)
 * @param {string} key
 * @param {Record<string, string>} [fmsMap]
 * @param {import('i18next').TFunction} [t]
 */
export function resolveLabelForFieldKey(key, fmsMap = {}, t = null) {
  if (key == null || key === '') return '';
  const fromFms = lookupFmsLabel(fmsMap, key);
  if (fromFms) return fromFms;
  const fromStatic = lookupStaticFieldLabel(key, t);
  if (fromStatic) return fromStatic;
  return humanizeKey(key);
}

/** @deprecated resolveLabelForFieldKey 사용 권장 */
export function fieldLabelKo(key, fmsMap) {
  return resolveLabelForFieldKey(key, fmsMap || {});
}
